import React, { useCallback, useEffect, useState } from 'react';
import Container from '@/components/Container';
import { Button, SelectChangeEvent, Typography } from '@mui/material';
import { BlockType } from '@/views/AnalyticsView/BlockTypes';
import { Page } from '@/declarations/models/Page';
import { useTranslation } from 'react-i18next';
import { useStore } from '@/components/store/Store';
import { Api } from '@/services/Api';
import { LoadingGif } from '@/components/LoadingGif';
import { AnalysisTable } from '@/views/AnalyticsView/AnalysisTable';
import { BlockTypeSelector } from '@/components/BlockTypeSelector';
import ConfusedLookingForByLooneyTunes from '@/assets/gifs/lookingFor/ConfusedLookingForByLooneyTunes.gif';
import ConfusedStephenColbert from '@/assets/gifs/lookingFor/ConfusedStephenColbert.gif';
import JoeBidenReactionLookingFor from '@/assets/gifs/lookingFor/JoeBidenReactionLookingFor.gif';
import NBALookingFor from '@/assets/gifs/lookingFor/NBALookingFor.gif';
import SquidGameLookingFor from '@/assets/gifs/lookingFor/SquidGameLookingFor.gif';
import CalculateZachGalifianakis from '@/assets/gifs/thinking-hard/CalculateZachGalifianakis.gif';
import CalculatingFigureItOut from '@/assets/gifs/thinking-hard/CalculatingFigureItOut.gif';
import ConfusedThinking from '@/assets/gifs/thinking-hard/ConfusedThinking.gif';
import BoredDailyShow from '@/assets/gifs/waiting/BoredDailyShow.gif';
import MrBeanWaiting from '@/assets/gifs/waiting/MrBeanWaiting.gif';
import MrBeanWaiting2 from '@/assets/gifs/waiting/MrBeanWaiting2.gif';
import WaitGoodbye from '@/assets/gifs/waiting/WaitGoodbye.gif';
import TheGrinchWaiting from '@/assets/gifs/waiting/TheGrinchWaiting.gif';
import ItsBeenALongTimeWaiting from '@/assets/gifs/waiting/ItsBeenALongTimeWaiting.gif';
import Loader from '@/components/Loader';

const loadingGifs = [
  ConfusedLookingForByLooneyTunes,
  ConfusedStephenColbert,
  JoeBidenReactionLookingFor,
  NBALookingFor,
  SquidGameLookingFor,
  CalculateZachGalifianakis,
  CalculatingFigureItOut,
  ConfusedThinking,
  BoredDailyShow,
  MrBeanWaiting,
  MrBeanWaiting2,
  WaitGoodbye,
  TheGrinchWaiting,
  ItsBeenALongTimeWaiting,
];

const getSanitizedUrl = (url: string) => {
  let sanitizedUrl = url.trim();
  if (!/^https?:\/\//i.test(sanitizedUrl)) {
    sanitizedUrl = `http://${sanitizedUrl.replace(/^[./]+/, '')}`;
  }
  try {
    return new URL(sanitizedUrl).href;
  } catch {
    return null;
  }
};

export const BlockTypeUsageAnalyzer = () => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const store = useStore();
  const currentSiteId = store?.state?.selectedSite?.id || 0;

  const [pagesData, setPagesData] = useState<{ pages: Page[]; pages_count: number; sites_count: number }>({
    pages: [],
    pages_count: 0,
    sites_count: 0,
  });
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(false);
  const [blockType, setBlockType] = useState<BlockType | ''>('');

  const fetchPages = useCallback(() => {
    setLoading(true);
    setHasFetched(true);
    if (blockType !== '') {
      const request = Api.getPagesForAnalysisAndStatistics([currentSiteId], blockType as BlockType);
      request
        .fetchDirect([])
        .then((fetched) => {
          if (Array.isArray(fetched)) {
            setPagesData({ pages: fetched, pages_count: fetched.length, sites_count: 0 });
          } else if (fetched && 'pages' in fetched) {
            setPagesData(fetched as { pages: Page[]; pages_count: number; sites_count: number });
          } else {
            setPagesData({ pages: [], pages_count: 0, sites_count: 0 });
          }
        })
        .finally(() => setLoading(false));
      return () => request.abort();
    }
  }, [currentSiteId, blockType]);

  const handleBlockTypeChange = (event: SelectChangeEvent<string>) => {
    setBlockType(event.target.value as BlockType);
  };

  // Prepare block type options for BlockTypeSelector
  const blockTypeOptions = Object.entries(BlockType)
    .filter(
      ([key, value]) =>
        key !== 'HEADER_MENU' &&
        !key.startsWith('FOOTER_') &&
        typeof value === 'string' &&
        !value.toString().startsWith('footer__'),
    )
    .map(([key, value]) => ({
      key,
      value,
      label: tCommon(`BlockType.${value}`),
    }));

  const isAnalyseDisabled = blockType === '';

  return (
    // <Container left fullWidth p={2} pt={0} top column>
    <Container left fullWidth p={0} pt={2} top column>
      <Typography>{tComp('AnalyticsView.BlockTypeUsageAnalyzer.DescriptiveText')}</Typography>
      <Container left fullWidth top mt={0}>
        <Container left mt={2} column sx={{ width: 400, maxHeight: 60 }}>
          <BlockTypeSelector
            blockType={blockType}
            onChange={handleBlockTypeChange}
            blockTypeOptions={blockTypeOptions}
          />
        </Container>
        <Container left mt={3}>
          <Button
            variant='contained'
            fullWidth
            onClick={fetchPages}
            color='success'
            disabled={isAnalyseDisabled || loading}>
            {tComp('AnalyticsView.BlockTypeUsageAnalyzer.RunAnalysisButton')}
          </Button>
        </Container>
      </Container>

      {hasFetched &&
        (loading ? (
          <Container fullWidth pt={3} top mt={0} column sx={{ height: '80vh' }}>
            <Loader />
          </Container>
        ) : (
          <Container left fullWidth pt={3} top mt={0} column>
            <AnalysisTable
              pages={pagesData.pages}
              pagesCount={pagesData.pages_count}
              tComp={tComp}
              getSanitizedUrl={getSanitizedUrl}
            />
          </Container>
        ))}
    </Container>
  );
};

export default BlockTypeUsageAnalyzer;
